import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderSlot as _renderSlot, createElementBlock as _createElementBlock, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps } from "vue"

const _hoisted_1 = { class: "rounded-lg border border-neutralLight" }
const _hoisted_2 = { class: "flex items-center justify-between px-4 py-3 rounded-t-lg bg-[#F0F0F4]" }
const _hoisted_3 = { class: "flex items-center gap-2" }
const _hoisted_4 = { class: "mb-2 last:mb-0" }
const _hoisted_5 = {
  key: 0,
  class: "flex items-center gap-3 px-3 h-16 rounded-lg border border-neutralLight bg-[#F0F0F4]"
}
const _hoisted_6 = { class: "text-sm font-bold text-primary50 whitespace-nowrap" }

import { computed, nextTick, ref } from 'vue'
import draggable from 'vuedraggable'
import { mdiDragVertical, mdiClose, mdiPlus } from '@mdi/js'

import RuleBuilderOperator from './RuleBuilderOperator.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'

import type {
  RuleBuilderGroup as RuleBuilderGroupType,
  RuleBuilderChild,
  RuleBuilderConfig
} from './types'

interface Props {
  group: RuleBuilderGroupType<string, unknown>
  config: RuleBuilderConfig<string>
  depth?: number
  operatorLabel?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'RuleBuilderGroup',
  props: {
    group: {},
    config: {},
    depth: { default: 0 },
    operatorLabel: { default: 'Operador' }
  },
  emits: ["update:group", "remove"],
  setup(__props: any, { emit }) {

const props = __props;



const childrenContainerRef = ref<HTMLElement | null>(null)

const resolvedMaxDepth = props.config.maxDepth ?? 3

// Poucos filhos para haver o que combinar, ou o consumidor travou este nível.
const isOperatorDisabled = computed(
  () =>
    props.group.children.length < (props.config.minChildrenForOperator ?? 2) ||
    (props.config.isOperatorLocked?.(props.depth) ?? false)
)

function resolvedRuleLabel(ruleNumber: number): string {
  return props.config.ruleLabel?.(ruleNumber) ?? `Regra ${ruleNumber}`
}

/* eslint-disable sonarjs/no-duplicate-string */


async function scrollToNewChild() {
  await nextTick()
  const container = childrenContainerRef.value
  if (!container) return
  const draggableRoot = container.firstElementChild as HTMLElement | null
  if (!draggableRoot) return
  const lastChild = draggableRoot.lastElementChild as HTMLElement | null
  if (!lastChild) return
  lastChild.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  lastChild.classList.add('rule-builder-item-highlight')
  lastChild.addEventListener(
    'animationend',
    () => lastChild.classList.remove('rule-builder-item-highlight'),
    { once: true }
  )
}

function addRule() {
  emit('update:group', {
    ...props.group,
    children: [...props.group.children, { type: 'rule' as const, value: null }]
  })
  scrollToNewChild()
}

function addGroup() {
  emit('update:group', {
    ...props.group,
    children: [
      ...props.group.children,
      {
        type: 'group' as const,
        operator: props.config.defaultOperator,
        children: [{ type: 'rule' as const, value: null }]
      }
    ]
  })
  scrollToNewChild()
}

async function removeChild(index: number) {
  const child = props.group.children[index]
  const callback =
    child.type === 'rule' ? props.config.beforeRemoveRule : props.config.beforeRemoveGroup

  if (callback) {
    const allowed = await callback(child)
    if (!allowed) return
  }

  emit('update:group', {
    ...props.group,
    children: props.group.children.filter((_, i) => i !== index)
  })
}

function updateChild(index: number, updated: RuleBuilderChild<string, unknown>) {
  const newChildren = [...props.group.children]
  newChildren[index] = updated
  emit('update:group', { ...props.group, children: newChildren })
}

function updateRule(index: number, value: unknown) {
  const child = props.group.children[index]
  if (child.type === 'rule') {
    updateChild(index, { ...child, value })
  }
}

function getRuleNumber(childIndex: number): number {
  let count = 1
  for (let i = 0; i < childIndex; i++) {
    if (props.group.children[i].type === 'rule') count++
  }
  return count
}

function handleReorder(newChildren: RuleBuilderChild<string, unknown>[]) {
  emit('update:group', { ...props.group, children: newChildren })
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_RuleBuilderGroup = _resolveComponent("RuleBuilderGroup", true)!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(RuleBuilderOperator, {
        "model-value": _ctx.group.operator,
        options: _ctx.config.operators,
        disabled: isOperatorDisabled.value,
        label: _ctx.operatorLabel,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = (val) => emit('update:group', { ..._ctx.group, operator: val }))
      }, null, 8, ["model-value", "options", "disabled", "label"]),
      _createElementVNode("div", _hoisted_3, [
        (_ctx.depth < _unref(resolvedMaxDepth))
          ? (_openBlock(), _createBlock(OutlinedButton, {
              key: 0,
              text: _ctx.config.addGroupLabel ?? 'Novo grupo',
              width: "112px",
              height: "32px",
              onClick: addGroup
            }, null, 8, ["text"]))
          : _createCommentVNode("", true),
        _createVNode(FilledButton, {
          text: _ctx.config.addRuleLabel ?? 'Nova regra',
          width: "135px",
          height: "34px",
          class: "bg-primary30",
          "prepend-icon": _unref(mdiPlus),
          onClick: addRule
        }, null, 8, ["text", "prepend-icon"]),
        (_ctx.depth > 0)
          ? (_openBlock(), _createBlock(_component_v_btn, {
              key: 1,
              icon: "",
              variant: "text",
              size: "small",
              density: "compact",
              "data-testid": "group-btn-remove",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('remove')))
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, {
                  icon: _unref(mdiClose),
                  size: "20",
                  class: "text-gray-400"
                }, null, 8, ["icon"])
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("div", {
      ref_key: "childrenContainerRef",
      ref: childrenContainerRef,
      class: "p-4"
    }, [
      _createVNode(_unref(draggable), {
        "model-value": _ctx.group.children,
        "item-key": "_index",
        handle: ".drag-handle",
        "ghost-class": "opacity-30",
        animation: 200,
        disabled: !(_ctx.config.allowDragDrop ?? true),
        "onUpdate:modelValue": handleReorder
      }, {
        item: _withCtx(({ element, index }) => [
          _createElementVNode("div", _hoisted_4, [
            (element.type === 'rule')
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createVNode(_component_v_icon, {
                    icon: _unref(mdiDragVertical),
                    class: "drag-handle cursor-grab text-gray-400",
                    size: "20"
                  }, null, 8, ["icon"]),
                  _createElementVNode("span", _hoisted_6, _toDisplayString(resolvedRuleLabel(getRuleNumber(index))), 1),
                  _createElementVNode("div", null, [
                    _renderSlot(_ctx.$slots, "rule", {
                      rule: element,
                      index: index,
                      update: (val) => updateRule(index, val),
                      remove: () => removeChild(index),
                      removable: _ctx.config.isRuleRemovable ? _ctx.config.isRuleRemovable(element) : true
                    })
                  ]),
                  (!_ctx.config.isRuleRemovable || _ctx.config.isRuleRemovable(element))
                    ? (_openBlock(), _createBlock(_component_v_btn, {
                        key: 0,
                        icon: "",
                        variant: "text",
                        size: "small",
                        density: "compact",
                        class: "ml-auto",
                        "data-testid": "rule-btn-remove",
                        onClick: ($event: any) => (removeChild(index))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_icon, {
                            icon: _unref(mdiClose),
                            size: "24",
                            class: "text-textPrimary"
                          }, null, 8, ["icon"])
                        ]),
                        _: 2
                      }, 1032, ["onClick"]))
                    : _createCommentVNode("", true)
                ]))
              : (_openBlock(), _createBlock(_component_RuleBuilderGroup, {
                  key: 1,
                  group: element,
                  config: _ctx.config,
                  depth: _ctx.depth + 1,
                  "operator-label": _ctx.operatorLabel,
                  "onUpdate:group": (updated) => updateChild(index, updated),
                  onRemove: ($event: any) => (removeChild(index))
                }, {
                  rule: _withCtx((slotProps) => [
                    _renderSlot(_ctx.$slots, "rule", _normalizeProps(_guardReactiveProps(slotProps)))
                  ]),
                  _: 2
                }, 1032, ["group", "config", "depth", "operator-label", "onUpdate:group", "onRemove"]))
          ])
        ]),
        _: 3
      }, 8, ["model-value", "disabled"])
    ], 512)
  ]))
}
}

})